/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import classNames from 'classnames';
import React from 'react';
import { Table } from 'reactstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  sortableTable,
  sortableTableTd,
  sortableTableTh,
  td,
  th,
  evenRow,
  oddRow,
  tableContainer,
} from './SortableTable.module.scss';

type SortingItem = {
  id: string,
  desc: boolean,
};

type Props = {|
  data: Array<any>,
  columns: Array<any>,
  initialSorting?: Array<SortingItem>,
  className?: string,
  getRowClassName?: (row: any, index: number) => string,
  getRowId?: ?(row: any, index: number) => string,
  tableId?: ?string,
  onSortingChange?: ?(sorting: Array<SortingItem>) => void,
  emptyMessage?: ?string,
  renderEmpty?: ?() => Element<any>,
  fixedHeight?: ?string,
|};

const SortableTable = ({
  data,
  columns,
  initialSorting = [],
  className = '',
  getRowClassName = () => '',
  getRowId = null,
  tableId = null,
  onSortingChange = null,
  emptyMessage = 'No data available',
  renderEmpty = null,
  fixedHeight = null,
}: Props): Element<typeof Table> | Element<'div'> => {
  // Ensure all columns start with ascending sort by default
  const memoizedColumns = React.useMemo(
    () =>
      columns.map(column => ({
        ...column,
        sortDescFirst: column.sortDescFirst !== undefined ? column.sortDescFirst : false,
      })),
    [columns]
  );
  const memoizedData = React.useMemo(() => data, [data]);
  const [sorting, setSorting] = React.useState(initialSorting);

  const tbodyId = tableId ? `${tableId}-tbody` : undefined;

  // Handle sorting changes with optional callback
  const handleSortingChange = React.useCallback(
    updaterOrValue => {
      setSorting(prevSorting => {
        const newSorting =
          typeof updaterOrValue === 'function' ? updaterOrValue(prevSorting) : updaterOrValue;

        // Call the optional callback if provided
        if (onSortingChange) {
          onSortingChange(newSorting);
        }

        return newSorting;
      });
    },
    [onSortingChange]
  );

  const table = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    ...(getRowId ? { getRowId } : {}),
  });

  const tableElement = (
    <Table bordered hover className={classNames([className, sortableTable])} id={tableId}>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id} className="sort-table-tr">
            {headerGroup.headers.map(header => {
              const { meta } = header.column.columnDef;
              const canSort = header.column.getCanSort();
              const sortDirection = header.column.getIsSorted();
              const sortClass = sortDirection ? `sort-${sortDirection}` : '';
              return (
                <th
                  key={header.id}
                  className={classNames(
                    'sort-table-th',
                    header.id,
                    meta?.headerClassName,
                    th,
                    sortableTableTh,
                    sortClass
                  )}
                  style={{ cursor: canSort ? 'pointer' : 'default' }}
                  onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {canSort && header.column.getIsSorted() === 'asc' && ' ▲'}
                  {canSort && header.column.getIsSorted() === 'desc' && ' ▼'}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody id={tbodyId} className="sort-table-tbody">
        {table.getRowModel().rows.length === 0 ? (
          <tr className="sort-table-tr">
            <td
              colSpan={columns.length}
              className="text-center text-muted sort-table-td"
              style={{ padding: '2rem' }}
            >
              {renderEmpty ? renderEmpty() : emptyMessage}
            </td>
          </tr>
        ) : (
          table.getRowModel().rows.map((row, index) => {
            const customRowClassName = getRowClassName(row.original, index);
            const zebraStripe = index % 2 === 0 ? evenRow : oddRow;
            const simpleZebraClass = index % 2 === 0 ? 'even' : 'odd';
            return (
              <tr
                key={row.id}
                id={row.id}
                className={classNames(
                  'sort-table-tr',
                  zebraStripe,
                  simpleZebraClass,
                  customRowClassName
                )}
              >
                {row.getVisibleCells().map(cell => {
                  const { meta } = cell.column.columnDef;
                  return (
                    <td
                      key={cell.id}
                      className={classNames(
                        'sort-table-td',
                        cell.column.id,
                        meta?.className,
                        td,
                        sortableTableTd
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}
      </tbody>
    </Table>
  );

  if (fixedHeight) {
    return (
      <div className={tableContainer} style={{ height: fixedHeight, overflow: 'auto' }}>
        {tableElement}
      </div>
    );
  }

  return tableElement;
};

export default SortableTable;
